import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, XIcon, CalendarIcon, ClockIcon, CheckCircleIcon, XCircleIcon, EditIcon, ArchiveIcon, UserIcon, CreditCardIcon, LandmarkIcon, WalletIcon, FileTextIcon, ShieldCheckIcon, AlertCircleIcon, DownloadIcon, SmartphoneIcon, PaperclipIcon } from 'lucide-react';
import { mockBills, approveBill, rejectBill, updateBill } from '../utils/mockBills';
import { mockSuppliers } from '../utils/mockSuppliers';
export function BillDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [, forceUpdate] = useState(0);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const bill = mockBills.find(b => b.id === id) || mockBills[0];
  const currentUser = 'Simon Verraest';
  // Look up supplier for payout details
  const supplier = mockSuppliers.find(s => s.id === bill.supplierId);
  // Derived state logic
  const isScheduled = bill.status === 'approved' && bill.approvalStatus === 'approved';
  const isDue = bill.status === 'approved' && !isScheduled;
  const isPending = bill.status === 'pending_approval';
  const isPaid = bill.status === 'paid';
  const isRejected = bill.status === 'rejected';
  const isOverdue = bill.status === 'overdue';
  const isAssignedToMe = bill.approver === currentUser;
  const displayStatus = isPaid ? 'Paid' : isRejected ? 'Rejected' : isScheduled ? 'Scheduled' : isPending ? 'Pending Approval' : isOverdue ? 'Overdue' : isDue ? 'Due' : 'Draft';
  const getStatusBadgeClass = () => {
    if (isPaid) return 'bg-green-50 text-green-700 border-green-200';
    if (isRejected || isOverdue) return 'bg-red-50 text-red-700 border-red-200';
    if (isScheduled) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (isPending) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };
  // Handlers
  const handleApprove = () => {
    approveBill(bill.id, currentUser);
    forceUpdate(n => n + 1);
  };
  const handleReject = () => {
    rejectBill(bill.id, currentUser, rejectNotes);
    setShowRejectModal(false);
    setRejectNotes('');
    forceUpdate(n => n + 1);
  };
  const handleArchive = () => {
    updateBill(bill.id, {
      status: 'rejected',
      approvalNotes: 'Archived by user'
    });
    forceUpdate(n => n + 1);
  };
  const handleCancelSchedule = () => {
    updateBill(bill.id, {
      status: 'approved',
      approvalStatus: 'pending',
      approver: undefined
    });
    forceUpdate(n => n + 1);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: bill.currency,
      minimumFractionDigits: 2
    }).format(amount);
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  // Helper: get payout method display details from supplier
  const getPayoutDetails = () => {
    if (!supplier) return null;
    if (supplier.payoutMethod === 'bank_account') {
      return {
        type: 'Bank Account',
        icon: <LandmarkIcon size={16} className="text-gray-400" data-id="element-5139" />,
        details: [{
          label: 'Bank',
          value: supplier.bankName || '—'
        }, {
          label: 'Account',
          value: supplier.accountNumber ? '•••' + supplier.accountNumber.slice(-4) : '—'
        }, {
          label: 'Account Name',
          value: supplier.accountName || '—'
        }]
      };
    }
    return {
      type: 'PromptPay',
      icon: <SmartphoneIcon size={16} className="text-gray-400" data-id="element-5140" />,
      details: [{
        label: 'Type',
        value: supplier.promptPayType === 'phone' ? 'Phone Number' : supplier.promptPayType === 'citizen_id' ? 'Citizen ID' : 'Tax ID'
      }, {
        label: 'ID',
        value: supplier.promptPayId ? supplier.promptPayId.slice(0, 3) + '••••' + supplier.promptPayId.slice(-3) : '—'
      }, {
        label: 'Name',
        value: supplier.promptPayName || '—'
      }]
    };
  };
  // Helper: get payment source display
  const getPaymentSourceLabel = () => {
    switch (bill.paymentSource) {
      case 'balance':
        return 'Pomelo Balance';
      case 'card':
        return 'Corporate Card';
      case 'bank_transfer':
        return 'Bank Transfer';
      default:
        return 'Unknown';
    }
  };
  const getPaymentSourceIcon = () => {
    switch (bill.paymentSource) {
      case 'balance':
        return <WalletIcon size={16} className="text-gray-400" data-id="element-5141" />;
      case 'card':
        return <CreditCardIcon size={16} className="text-gray-400" data-id="element-5142" />;
      default:
        return <LandmarkIcon size={16} className="text-gray-400" data-id="element-5143" />;
    }
  };
  // ===== TIMELINE LOGIC (corrected) =====
  // Step statuses: 'completed' | 'current' | 'future' | 'rejected'
  const approvalCompleted = bill.approvalStatus === 'approved' || isScheduled || isPaid || isDue; // isDue means status='approved', so approval happened
  const approvalRejected = bill.approvalStatus === 'rejected' || isRejected;
  const steps = [{
    id: 'created',
    label: 'Bill Created',
    date: bill.issueDate,
    actor: bill.createdBy || currentUser,
    status: 'completed' as const
  }, {
    id: 'approval',
    label: 'Approval',
    date: approvalCompleted || approvalRejected ? bill.approvalDate : undefined,
    actor: approvalCompleted ? bill.approver || 'Auto-approved' : approvalRejected ? bill.approver || 'System' : isPending ? bill.approver || 'Awaiting assignment' : '—',
    status: approvalCompleted ? 'completed' as const : approvalRejected ? 'rejected' as const : isPending ? 'current' as const : 'future' as const
  }, {
    id: 'scheduled',
    label: 'Scheduled',
    date: isScheduled || isPaid ? bill.dueDate : undefined,
    actor: isScheduled || isPaid ? 'System' : undefined,
    status: isScheduled || isPaid ? 'completed' as const : 'future' as const
  }, {
    id: 'payment',
    label: 'Payment',
    date: isPaid ? bill.paidDate : undefined,
    actor: isPaid ? getPaymentSourceLabel() : undefined,
    status: isPaid ? 'completed' as const : isScheduled ? 'current' as const : 'future' as const
  }];
  const payoutDetails = getPayoutDetails();
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-5144">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-5145">
        <button onClick={() => navigate('/bills')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm transition-colors" data-id="element-5146">
          <ArrowLeftIcon size={18} data-id="element-5147" />
          Back to Bills
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-5148">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-2 space-y-6" data-id="element-5149">
          {/* Card 1: Header & Total */}
          <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm" data-id="element-5150">
            <div className="flex justify-between items-start mb-6" data-id="element-5151">
              <div data-id="element-5152">
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2" data-id="element-5153">
                  {bill.billNumber}
                </h1>
                <p className="text-gray-500" data-id="element-5154">
                  {bill.description || 'No description provided'}
                </p>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadgeClass()}`} data-id="element-5155">
                {displayStatus}
              </span>
            </div>
            <div className="pt-6 border-t border-gray-100" data-id="element-5156">
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-5157">
                Total Amount
              </div>
              <div className="text-4xl font-bold text-gray-900" data-id="element-5158">
                {formatCurrency(bill.total)}
              </div>
            </div>
          </div>

          {/* Card: Dates */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-5159">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-5160">
              <div data-id="element-5161">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-5162">
                  Issue Date
                </div>
                <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-5163">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-5164" />
                  {formatDate(bill.issueDate)}
                </div>
              </div>
              <div data-id="element-5165">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-5166">
                  Due Date
                </div>
                <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-5167">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-5168" />
                  {formatDate(bill.dueDate)}
                </div>
              </div>
              {bill.approvalDate && approvalCompleted && <div data-id="element-5169">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-5170">
                    Approval Date
                  </div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-5171">
                    <CheckCircleIcon size={14} className="text-gray-400" data-id="element-5172" />
                    {formatDate(bill.approvalDate)}
                  </div>
                </div>}
              {bill.paidDate && <div data-id="element-5173">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-5174">
                    Paid Date
                  </div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-5175">
                    <CheckCircleIcon size={14} className="text-green-500" data-id="element-5176" />
                    {formatDate(bill.paidDate)}
                  </div>
                </div>}
            </div>
          </div>

          {/* Card: Supplier */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-5177">
            <div className="flex justify-between items-center mb-6" data-id="element-5178">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2" data-id="element-5179">
                <UserIcon size={16} className="text-gray-400" data-id="element-5180" />
                Supplier Details
              </h3>
              <button onClick={() => navigate(`/supplier-detail/${bill.supplierId}`)} className="text-sm font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors" data-id="element-5181">
                View Profile &rarr;
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5182">
              <div data-id="element-5183">
                <div className="text-xs text-gray-500 mb-1" data-id="element-5184">Company Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5185">
                  {bill.supplierName}
                </div>
              </div>
              <div data-id="element-5186">
                <div className="text-xs text-gray-500 mb-1" data-id="element-5187">Contact Email</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5188">
                  {bill.supplierEmail}
                </div>
              </div>
              {bill.supplierPhone && <div data-id="element-5189">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-5190">Phone Number</div>
                  <div className="text-sm font-medium text-gray-900" data-id="element-5191">
                    {bill.supplierPhone}
                  </div>
                </div>}
            </div>
          </div>

          {/* Card: Payment Information (Paid/Scheduled) */}
          {(isPaid || isScheduled) && <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-5192">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 mb-6" data-id="element-5193">
                <CreditCardIcon size={16} className="text-gray-400" data-id="element-5194" />
                Payment Information
              </h3>

              {isPaid ? <div className="space-y-6" data-id="element-5195">
                  {/* Payment Source */}
                  <div data-id="element-5196">
                    <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3" data-id="element-5197">
                      Funded From
                    </div>
                    <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-5198">
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-5199">
                        {getPaymentSourceIcon()}
                      </div>
                      <div data-id="element-5200">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-5201">
                          {getPaymentSourceLabel()}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-5202">
                          Paid on {formatDate(bill.paidDate)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payout Destination */}
                  {payoutDetails && <div data-id="element-5203">
                      <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3" data-id="element-5204">
                        Paid To
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-5205">
                        <div className="flex items-center gap-3 mb-4" data-id="element-5206">
                          <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-5207">
                            {payoutDetails.icon}
                          </div>
                          <div className="text-sm font-semibold text-gray-900" data-id="element-5208">
                            {payoutDetails.type}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-200" data-id="element-5209">
                          {payoutDetails.details.map(d => <div key={d.label} data-id="element-5210">
                              <div className="text-xs text-gray-500 mb-0.5" data-id="element-5211">
                                {d.label}
                              </div>
                              <div className="text-sm font-medium text-gray-900" data-id="element-5212">
                                {d.value}
                              </div>
                            </div>)}
                        </div>
                      </div>
                    </div>}
                </div> : <div className="bg-blue-50 text-blue-800 p-4 rounded-lg flex items-start gap-3 border border-blue-100" data-id="element-5213">
                  <ClockIcon size={18} className="text-blue-600 mt-0.5" data-id="element-5214" />
                  <div data-id="element-5215">
                    <div className="font-medium text-sm" data-id="element-5216">
                      Scheduled for Payment
                    </div>
                    <div className="text-xs mt-1 text-blue-700" data-id="element-5217">
                      This bill is scheduled to be paid on or before{' '}
                      {formatDate(bill.dueDate)}.
                    </div>
                  </div>
                </div>}
            </div>}

          {/* Card: Attachment (replaces Line Items) */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-5218">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 mb-6" data-id="element-5219">
              <PaperclipIcon size={16} className="text-gray-400" data-id="element-5220" />
              Attachment
            </h3>

            {bill.attachmentFileName ? <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-5221">
                <div className="flex items-center gap-3 min-w-0" data-id="element-5222">
                  <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0" data-id="element-5223">
                    <FileTextIcon size={20} className="text-red-500" data-id="element-5224" />
                  </div>
                  <div className="min-w-0" data-id="element-5225">
                    <div className="text-sm font-medium text-gray-900 truncate" data-id="element-5226">
                      {bill.attachmentFileName}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-5227">
                      Uploaded with bill
                    </div>
                  </div>
                </div>
                <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#F7941D] hover:bg-orange-50 rounded-lg transition-colors flex-shrink-0" data-id="element-5228">
                  <DownloadIcon size={16} data-id="element-5229" />
                  Download
                </button>
              </div> : <div className="text-center py-8 text-gray-400" data-id="element-5230">
                <PaperclipIcon size={24} className="mx-auto mb-2 opacity-50" data-id="element-5231" />
                <p className="text-sm" data-id="element-5232">No file attached to this bill</p>
              </div>}

            {/* Amount Summary */}
            <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end" data-id="element-5233">
              <div className="w-full max-w-xs space-y-3" data-id="element-5234">
                <div className="flex justify-between text-sm" data-id="element-5235">
                  <span className="text-gray-500" data-id="element-5236">Subtotal</span>
                  <span className="text-gray-900 font-medium" data-id="element-5237">
                    {formatCurrency(bill.subtotal)}
                  </span>
                </div>
                {bill.taxAmount && bill.taxAmount > 0 && <div className="flex justify-between text-sm" data-id="element-5238">
                    <span className="text-gray-500" data-id="element-5239">Tax</span>
                    <span className="text-gray-900 font-medium" data-id="element-5240">
                      {formatCurrency(bill.taxAmount)}
                    </span>
                  </div>}
                {bill.discountAmount && bill.discountAmount > 0 && <div className="flex justify-between text-sm" data-id="element-5241">
                    <span className="text-gray-500" data-id="element-5242">Discount</span>
                    <span className="text-red-600 font-medium" data-id="element-5243">
                      -{formatCurrency(bill.discountAmount)}
                    </span>
                  </div>}
                <div className="flex justify-between pt-3 border-t border-gray-200" data-id="element-5244">
                  <span className="text-base font-bold text-gray-900" data-id="element-5245">
                    Total
                  </span>
                  <span className="text-base font-bold text-gray-900" data-id="element-5246">
                    {formatCurrency(bill.total)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Card: Notes */}
          {bill.notes && <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-5247">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-5248">
                Notes
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700 border border-gray-100" data-id="element-5249">
                {bill.notes}
              </div>
            </div>}
        </div>

        {/* RIGHT COLUMN */}
        <div className="lg:col-span-1 space-y-6" data-id="element-5250">
          {/* ACTIONS CARD */}
          <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${isPending && isAssignedToMe ? 'border-l-4 border-l-[#F7941D] border-y-gray-200 border-r-gray-200' : 'border-gray-200'}`} data-id="element-5251">
            <div className="p-6" data-id="element-5252">
              {/* DUE */}
              {isDue && <>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-5" data-id="element-5253">
                    Actions
                  </h3>
                  <div className="space-y-3" data-id="element-5254">
                    <button onClick={() => navigate(`/bill-approval/schedule?ids=${bill.id}`)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#F7941D] text-white rounded-lg hover:bg-[#D97B06] text-sm font-medium shadow-sm transition-colors" data-id="element-5255">
                      <CalendarIcon size={16} data-id="element-5256" />
                      Schedule Payment
                    </button>
                    <button onClick={handleArchive} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-5257">
                      <ArchiveIcon size={16} data-id="element-5258" />
                      Archive Bill
                    </button>
                    <button onClick={() => navigate(`/bill-form?id=${bill.id}`)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[#F7941D] hover:bg-orange-50 rounded-lg text-sm font-medium transition-colors mt-2" data-id="element-5259">
                      <EditIcon size={16} data-id="element-5260" />
                      Edit Details
                    </button>
                  </div>
                </>}

              {/* PENDING */}
              {isPending && <>
                  {isAssignedToMe ? <>
                      <div className="flex items-center gap-2 mb-3" data-id="element-5261">
                        <ShieldCheckIcon size={18} className="text-[#F7941D]" data-id="element-5262" />
                        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-5263">
                          Approval Required
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-6" data-id="element-5264">
                        This bill has been assigned to you. Please review the
                        details and approve or reject.
                      </p>
                      <div className="space-y-3" data-id="element-5265">
                        <button onClick={handleApprove} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#F7941D] text-white rounded-lg hover:bg-[#D97B06] text-sm font-medium shadow-sm transition-colors" data-id="element-5266">
                          <CheckIcon size={16} data-id="element-5267" />
                          Approve Bill
                        </button>
                        <button onClick={() => setShowRejectModal(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 text-sm font-medium transition-colors" data-id="element-5268">
                          <XIcon size={16} data-id="element-5269" />
                          Reject Bill
                        </button>
                      </div>
                    </> : <>
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-5270">
                        Status
                      </h3>
                      <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4 flex items-start gap-3" data-id="element-5271">
                        <ClockIcon size={18} className="text-yellow-600 mt-0.5" data-id="element-5272" />
                        <div data-id="element-5273">
                          <div className="text-sm font-medium text-yellow-900" data-id="element-5274">
                            Awaiting Approval
                          </div>
                          <div className="text-xs text-yellow-700 mt-1" data-id="element-5275">
                            Assigned to:{' '}
                            <span className="font-semibold" data-id="element-5276">
                              {bill.approver || 'Unassigned'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>}
                </>}

              {/* SCHEDULED */}
              {isScheduled && <>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-5277">
                    Status
                  </h3>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3 mb-6" data-id="element-5278">
                    <CalendarIcon size={18} className="text-blue-600 mt-0.5" data-id="element-5279" />
                    <div data-id="element-5280">
                      <div className="text-sm font-medium text-blue-900" data-id="element-5281">
                        Payment Scheduled
                      </div>
                      <div className="text-xs text-blue-700 mt-1" data-id="element-5282">
                        Will be paid on {formatDate(bill.dueDate)}
                      </div>
                    </div>
                  </div>
                  <button onClick={handleCancelSchedule} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-5283">
                    <XIcon size={16} data-id="element-5284" />
                    Cancel Schedule
                  </button>
                </>}

              {/* PAID */}
              {isPaid && <>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-5285">
                    Status
                  </h3>
                  <div className="bg-green-50 border border-green-100 rounded-lg p-4 flex items-start gap-3" data-id="element-5286">
                    <CheckCircleIcon size={18} className="text-green-600 mt-0.5" data-id="element-5287" />
                    <div data-id="element-5288">
                      <div className="text-sm font-medium text-green-900" data-id="element-5289">
                        Payment Complete
                      </div>
                      <div className="text-xs text-green-700 mt-1" data-id="element-5290">
                        Successfully paid on {formatDate(bill.paidDate)}
                      </div>
                    </div>
                  </div>
                </>}

              {/* REJECTED */}
              {isRejected && <>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-5291">
                    Status
                  </h3>
                  <div className="bg-red-50 border border-red-100 rounded-lg p-4 flex items-start gap-3" data-id="element-5292">
                    <XCircleIcon size={18} className="text-red-600 mt-0.5" data-id="element-5293" />
                    <div data-id="element-5294">
                      <div className="text-sm font-medium text-red-900" data-id="element-5295">
                        Bill Rejected
                      </div>
                      <div className="text-xs text-red-700 mt-1" data-id="element-5296">
                        Rejected by {bill.approver || 'System'}
                      </div>
                    </div>
                  </div>
                  {bill.approvalNotes && <div className="mt-4 text-sm text-gray-600 bg-gray-50 p-3 rounded border border-gray-100" data-id="element-5297">
                      <span className="font-medium text-gray-900 block mb-1" data-id="element-5298">
                        Reason:
                      </span>
                      {bill.approvalNotes}
                    </div>}
                </>}
            </div>
          </div>

          {/* TIMELINE CARD */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-5299">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6" data-id="element-5300">
              Workflow Timeline
            </h3>

            <div className="space-y-0 relative" data-id="element-5301">
              {steps.map((step, index) => {
              const nextStep = steps[index + 1];
              // Line is dark only if current step is completed AND next step is also completed or current
              const lineDark = step.status === 'completed' && nextStep && (nextStep.status === 'completed' || nextStep.status === 'current');
              return <div key={step.id} className="flex gap-4 relative" data-id="element-5302">
                    {/* Connector Line */}
                    {index < steps.length - 1 && <div className={`absolute left-[11px] top-7 bottom-[-7px] w-0.5 ${lineDark ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-5303" />}

                    {/* Indicator */}
                    <div className="relative z-10 flex flex-col items-center mt-1" data-id="element-5304">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${step.status === 'completed' ? 'bg-gray-900 text-white' : step.status === 'current' ? 'bg-white border-2 border-[#F7941D]' : step.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-gray-100 border border-gray-200'}`} data-id="element-5305">
                        {step.status === 'completed' && <CheckIcon size={12} strokeWidth={3} data-id="element-5306" />}
                        {step.status === 'current' && <div className="w-2 h-2 rounded-full bg-[#F7941D]" data-id="element-5307" />}
                        {step.status === 'rejected' && <XIcon size={12} strokeWidth={3} data-id="element-5308" />}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="pb-8 min-w-0 flex-1" data-id="element-5309">
                      <div className="flex items-center justify-between" data-id="element-5310">
                        <span className={`text-sm font-medium ${step.status === 'future' ? 'text-gray-400' : step.status === 'rejected' ? 'text-red-700' : 'text-gray-900'}`} data-id="element-5311">
                          {step.label}
                        </span>
                        {step.date && step.status !== 'future' && <span className="text-xs text-gray-500" data-id="element-5312">
                            {formatDate(step.date)}
                          </span>}
                      </div>
                      {step.status === 'current' ? <div className="text-xs mt-1 text-[#F7941D] font-medium flex items-center gap-1" data-id="element-5313">
                          <ClockIcon size={12} data-id="element-5314" /> Awaiting
                        </div> : step.actor && step.status !== 'future' ? <div className="text-xs mt-1 text-gray-500" data-id="element-5315">
                          {step.actor}
                        </div> : null}
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowRejectModal(false)} data-id="element-5316" />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-5317">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-5318">
              <div className="p-8" data-id="element-5319">
                <div className="flex items-center gap-3 mb-4" data-id="element-5320">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-5321">
                    <AlertCircleIcon size={20} className="text-red-600" data-id="element-5322" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-5323">
                    Reject Bill
                  </h2>
                </div>
                <p className="text-sm text-gray-600 mb-6" data-id="element-5324">
                  Are you sure you want to reject bill{' '}
                  <span className="font-medium text-gray-900" data-id="element-5325">
                    {bill.billNumber}
                  </span>
                  ? This action will notify the submitter.
                </p>

                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5326">
                  Reason for rejection
                </label>
                <textarea value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} rows={3} placeholder="Please provide a reason..." className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-400 transition-colors resize-none" data-id="element-5327" />

                <div className="flex justify-end gap-3 mt-8" data-id="element-5328">
                  <button onClick={() => {
                setShowRejectModal(false);
                setRejectNotes('');
              }} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5329">
                    Cancel
                  </button>
                  <button onClick={handleReject} disabled={!rejectNotes.trim()} className={`px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${rejectNotes.trim() ? 'bg-red-600 hover:bg-red-700 shadow-sm' : 'bg-red-300 cursor-not-allowed'}`} data-id="element-5330">
                    Reject Bill
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}